import { useContext, useEffect, useRef, useState } from "react";
import { AppLayout } from "../../../containers/layout/app";
import { TellerPostingApprovalWrapper } from "./styled";
import { AppContext } from "../../../context/appContext";
import { TellerPostingApprovalTable } from "../../../containers/tables/tellerpostingapprovaltable";
import { useQuery } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { retrievePendingPostingService } from "../../../utils/api/transaction/transactionposting/retrievePendingPosting";
import {
	Box,
	CircularProgress,
	Grid2,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import { BaseConfirmationModal } from "../../../components/modal/confirmation";
import { actOnPendingPostingService } from "../../../utils/api/approval/tellerpostingapproval/actOnPendingPosting";
import { camelCaseToPascalCaseFormatter } from "../../../helpers/camelCaseToPascalCaseFormatter";
import { retrieveSavingsAccountByAccountNumberService } from "../../../utils/outbound/savingsaccount/retrieveSavingsAccountByAccountNumber";
import { retrieveChartOfAccountService } from "../../../utils/api/accounting/chartofaccount/retrieveChartOfAccount";
import { retrieveCustomerService } from "../../../utils/api/customerservice/customerlist/retrieveCustomer";
import { useDebounce } from "use-debounce";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { formatAmountDisplay } from "../../../helpers/formatAmountDisplay";
import { BaseButton } from "../../../components/button/styled";

export const TellerPostingApproval = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const { setActiveNavItem } = useContext(AppContext);
	const dropdownRef = useRef<HTMLUListElement | null>(null);

	const [action, setAction] = useState<string | null>(null);
	const [formDetails, setFormDetails] = useState({
		comment: "",
	});
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isLoadingAccountName, setIsLoadingAccountName] = useState(false);
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
	const [selectedTransaction, setSelectedTransaction] = useState<
		Record<string, any>
	>({
		id: "",
		institution: "",
		referenceType: "",
		reference: "",
		transactionType: "",
		amount: "",
		accountName: "",
	});
	const [debouncedTransactionReferenceRef] = useDebounce(
		selectedTransaction?.reference,
		500,
	);
	const [debouncedTransactionReferenceTypeRef] = useDebounce(
		selectedTransaction?.referenceType,
		500,
	);
	const [isFetching, setIsFetching] = useState(false);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});

	useEffect(() => {
		setActiveNavItem("Approval");
	}, [setActiveNavItem]);

	useEffect(() => {
		if (
			!debouncedTransactionReferenceRef ||
			!debouncedTransactionReferenceTypeRef
		)
			return;
		const controller = new AbortController();
		let isActive = true;
		const fetchReferenceInfo = async () => {
			try {
				let customer: any;
				let chartOfAccount: any;
				setIsLoadingAccountName(true);
				setSelectedTransaction((prev) => ({
					...prev,
					accountName: "",
				}));
				if (debouncedTransactionReferenceTypeRef === "bank") {
					const savingsAccount =
						await retrieveSavingsAccountByAccountNumberService(
							TOKEN,
							debouncedTransactionReferenceRef,
							controller.signal,
						);
					if (!isActive) return;
					if (!savingsAccount) {
						setSelectedTransaction((prev) => ({
							...prev,
							accountName: "",
						}));
						return;
					}
					customer = await retrieveCustomerService(
						TOKEN,
						savingsAccount?.ownerId,
						controller.signal,
					);
				} else {
					chartOfAccount = await retrieveChartOfAccountService(
						TOKEN,
						debouncedTransactionReferenceRef,
					);
					if (!isActive) return;
					if (!chartOfAccount) {
						setSelectedTransaction((prev) => ({
							...prev,
							accountName: "",
						}));
						return;
					}
				}
				if (!isActive) return;
				setSelectedTransaction((prev) => ({
					...prev,
					accountName:
						customer?.firstName || customer?.lastName
							? `${customer?.firstName ?? ""} ${customer?.lastName ?? ""}`.trim()
							: (chartOfAccount?.glName ?? ""),
				}));
			} catch (err: any) {
				if (err.name !== "AbortError") {
					console.error("Failed to fetch transaction information:", err);
					setSelectedTransaction((prev) => ({
						...prev,
						accountName: "",
					}));
				}
			} finally {
				if (isActive) setIsLoadingAccountName(false);
			}
		};
		fetchReferenceInfo();
		return () => {
			isActive = false;
			controller.abort();
		};
	}, [
		TOKEN,
		debouncedTransactionReferenceRef,
		debouncedTransactionReferenceTypeRef,
	]);

	const { data: tellerPostingsToBeApproved, refetch } = useQuery({
		queryKey: ["pendingTellerPostings", TOKEN, paginationIndex],
		queryFn: async () => {
			setIsFetching(true);
			const response = await retrievePendingPostingService(
				TOKEN,
				paginationIndex,
			);
			if (!Array.isArray(response) && response?.meta) {
				setPaginationIndex((prev) => ({
					...prev,
					totalPages: String(response?.meta?.totalPages ?? "1"),
				}));
			}
			const postings = response?.data || [];
			const referenceInfo = await Promise.all(
				postings.map(async (pendingTellerPosting: any) => {
					let reference;
					try {
						if (pendingTellerPosting?.referenceType === "bank") {
							const savingsAccount =
								await retrieveSavingsAccountByAccountNumberService(
									TOKEN,
									pendingTellerPosting.bankAccountReference,
								);
							if (!savingsAccount) return null;
							const customer = await retrieveCustomerService(
								TOKEN,
								savingsAccount?.ownerId,
							);
							reference = customer;
						} else {
							const chartOfAccount = await retrieveChartOfAccountService(
								TOKEN,
								pendingTellerPosting.glReference,
							);
							reference = chartOfAccount;
						}
						return {
							...pendingTellerPosting,
							accountName:
								reference?.firstName || reference?.lastName
									? `${reference?.firstName ?? ""} ${reference?.lastName ?? ""}`.trim()
									: (reference?.glName ?? ""),
						};
					} catch (error) {
						console.error(
							`Failed to retrieve reference information for posting with ID ${pendingTellerPosting.id}:`,
							error,
						);
						return {
							...pendingTellerPosting,
							referenceInformation: null,
						};
					}
				}),
			);
			setIsFetching(false);
			return postings;
		},
		enabled: !!TOKEN,
	});

	useEffect(() => {
		if (!isConfirmationModalOpen && TOKEN) {
			refetch();
		}
	}, [isConfirmationModalOpen, TOKEN, refetch]);

	const handleSelectTransaction = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		transaction: Record<string, any>,
	) => {
		e.stopPropagation();
		e.preventDefault();
		return setSelectedTransaction(transaction);
	};

	const handleOpenConfirmationModal = () => {
		return setIsConfirmationModalOpen(true);
	};

	const handleActionItemClick = (
		e: React.MouseEvent<HTMLLIElement, MouseEvent>,
		selectedTransactionId: string | null,
		action: string,
	) => {
		e.stopPropagation();
		e.preventDefault();
		if (!selectedTransactionId) return;
		setAction(action);
		return handleOpenConfirmationModal();
	};

	const handleConfirmationModalPersist = () => {
		return setIsConfirmationModalOpen(true);
	};

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleActionOnPendingPosting = async () => {
		if (isLoadingAccountName) return setIsLoading(false);
		try {
			const payload = {
				action,
				...(action === "Decline" ? { comment: formDetails.comment } : {}),
			};
			const response = await actOnPendingPostingService(
				TOKEN,
				selectedTransaction.institution,
				selectedTransaction.id,
				payload,
			);
			if (response.status !== "success") {
				setError(`${action} posting failed. Please try again.`);
			} else {
				setError(null);
			}
			setIsLoading(false);
			setAction(null);
			setFormDetails({
				comment: "",
			});
			setSelectedTransaction({
				id: "",
				institution: "",
				referenceType: "",
				reference: "",
				transactionType: "",
				amount: "",
				accountName: "",
			});
			return setIsConfirmationModalOpen(false);
		} catch (error: any) {
			console.error(`${action} posting failed:`, error);
			setIsLoading(false);
			return setError(`${action} posting failed. ${error.message}`);
		}
	};

	const handleConfirmationModalCallToActionClick = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		actionButton: string,
	) => {
		e.preventDefault();
		e.stopPropagation();
		setIsLoading(true);
		if (!action || !selectedTransaction.id) return;
		switch (actionButton) {
			case "callToActionI":
				setError(null);
				await handleActionOnPendingPosting();
				break;
			case "callToActionII":
				setIsLoading(false);
				setError(null);
				setAction(null);
				setFormDetails({
					comment: "",
				});
				setSelectedTransaction({
					id: "",
					institution: "",
					referenceType: "",
					reference: "",
					transactionType: "",
					amount: "",
					accountName: "",
				});
				setIsConfirmationModalOpen(false);
				break;
			default:
				break;
		}
	};

	const handleDropDownClickOutside = (event: MouseEvent) => {
		if (
			dropdownRef.current &&
			!dropdownRef.current.contains(event.target as Node)
		) {
			return setSelectedTransaction({
				id: "",
				institution: "",
				referenceType: "",
				reference: "",
				transactionType: "",
				amount: "",
				accountName: "",
			});
		}
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	useEffect(() => {
		// ensure that the empty string value of initial state is matched
		if (selectedTransaction.id !== "" && !isConfirmationModalOpen) {
			document.addEventListener("mousedown", handleDropDownClickOutside);
		} else {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		};
	}, [selectedTransaction, isConfirmationModalOpen]);

	const confirmationModalBody = (
		<Box>
			<Stack
				paddingTop={"calc(var(--basic-padding)/1.5)"}
				gap={{ mobile: "calc(var(--flex-gap)/2)" }}
			>
				{["reference", "transactionType", "amount"].map((key) => (
					<Stack
						key={key}
						direction={{ mobile: "row" }}
						gap={{ mobile: "calc(var(--flex-gap)/4)" }}
						justifyContent="space-between"
					>
						<Typography
							variant="h3"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={16}
							lineHeight={"normal"}
						>
							{key === "reference"
								? "Account"
								: camelCaseToPascalCaseFormatter(key)}
							:
						</Typography>
						<Typography
							variant="body1"
							fontFamily={"Inter"}
							fontWeight={500}
							fontSize={16}
							lineHeight={"normal"}
						>
							{key === "reference" ? (
								isLoadingAccountName ? (
									<CircularProgress color="inherit" className="loader" />
								) : (
									selectedTransaction["accountName"]
										?.split(" ")
										.map(
											(word: string) =>
												word.charAt(0).toUpperCase() + word.slice(1),
										)
										.join(" ")
								)
							) : key === "amount" ? (
								formatAmountDisplay(selectedTransaction[key])
							) : (
								selectedTransaction[key]
							)}
						</Typography>
					</Stack>
				))}
				{action === "Decline" && (
					<form>
						<BaseFieldSet sx={{ display: "flex", flexDirection: "column" }}>
							<BaseInput
								required
								multiline
								minRows={4}
								name="comment"
								value={formDetails.comment}
								placeholder="Enter reason for decline."
								onChange={(e) => handleChange(e)}
							/>
						</BaseFieldSet>
					</form>
				)}
			</Stack>
			{error && (
				<Typography
					fontFamily={"Inter"}
					fontWeight={"600"}
					fontSize={14}
					lineHeight={"normal"}
					color={"var(--text-salmon-pink-color)"}
					whiteSpace={"normal"}
					marginBlockStart={"calc(var(--basic-margin)/1)"}
				>
					{error}
				</Typography>
			)}
		</Box>
	);

	return (
		<AppLayout
			pageId={"Teller-Posting-Approval"}
			pageTitle={"Teller Posting Approval"}
		>
			<TellerPostingApprovalWrapper>
				{/* alert modal below */}
				<BaseConfirmationModal
					open={isConfirmationModalOpen}
					title="Confirm Approval"
					message="Are you sure you want to approve this transaction?"
					callToActionI="Confirm"
					callToActionII="Cancel"
					isLoadingI={isLoading}
					body={confirmationModalBody}
					handleClose={handleConfirmationModalPersist}
					handleCallToAction={handleConfirmationModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box component={"div"} className="pending-posting-table-box">
					<TellerPostingApprovalTable
						rows={tellerPostingsToBeApproved as Record<string, any>[]}
						dropdownRef={dropdownRef}
						selectedTransactionId={selectedTransaction.id}
						handleActionButtonClick={handleSelectTransaction}
						handleActionItemClick={handleActionItemClick}
					/>
				</Box>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFetching
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFetching
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
			</TellerPostingApprovalWrapper>
		</AppLayout>
	);
};
